import React from 'react';
import { AbstractButton, Box, Collapse, Flex, Icon, Text } from 'pounceV2';
import { useGetGeneralSettings } from 'Source/graphql/queries/getGeneralSettings.generated';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useTheme } from '@mui/material';
import { PRINT_BLOCK_TYPE_ATTR, PRINT_SUPPRESS_ATTR } from 'Components/utils/printAIResponse';
import { getMarkdownRenderers } from './EnrichedAIResponseV3';

type CollapsibleBlockProps = {
  value: string;
  title: string;
  blockType?: 'thinking' | 'analysis' | 'riskscore';
  isExpanded?: boolean;
  onSearchRequest?: (query: string) => void;
  /** Optional content to render after the title (e.g., badges, counts, labels) */
  titleExtra?: React.ReactNode;
};

export const THINKING_BLOCK_END = '===END_OF_THINKING_BLOCK===';
export const ANALYSIS_BLOCK_END = '===END_OF_ANALYSIS_BLOCK===';
export const RISK_SCORE_BLOCK_END = '===END_OF_RISK_SCORE_BLOCK===';

const CollapsibleBlockV3 = ({
  value,
  title,
  blockType,
  isExpanded: initialExpanded,
  onSearchRequest,
  titleExtra,
}: CollapsibleBlockProps) => {
  const {
    palette: { componentTokens, tokens },
  } = useTheme();
  // Process the value to remove the first tab from each line
  const [processedValue, blockIsCompleted] = React.useMemo(() => {
    if (!value) {
      return ['', false];
    }
    const lines = value.split('\n');
    const processedLines = lines.map(line => {
      if (line.startsWith('\t')) {
        return line.substring(1);
      }
      return line;
    });
    // Check for the specific end marker based on the block type
    let endMarker: string | null = null;
    if (blockType === 'thinking') {
      endMarker = THINKING_BLOCK_END;
    } else if (blockType === 'analysis') {
      endMarker = ANALYSIS_BLOCK_END;
    } else if (blockType === 'riskscore') {
      endMarker = RISK_SCORE_BLOCK_END;
    }

    if (endMarker && processedLines.includes(endMarker)) {
      return [processedLines.filter(line => line !== endMarker).join('\n'), true];
    }
    return [processedLines.join('\n'), false];
  }, [value, blockType]);

  const [expanded, setExpanded] = React.useState(initialExpanded ?? !blockIsCompleted);

  React.useEffect(() => {
    if (blockIsCompleted) {
      setExpanded(false);
    }
  }, [blockIsCompleted]);

  const { data } = useGetGeneralSettings();
  const isKendraEnabled = data?.generalSettings?.pantherAIKendra?.enabled;

  const renderers = React.useMemo(
    () =>
      getMarkdownRenderers({
        onSearchRequest,
        isKendraEnabled,
        riskData: null,
        componentTokens,
        tokens,
      }),
    [onSearchRequest, isKendraEnabled, componentTokens, tokens],
  );

  return (
    <Box
      {...(!expanded && { [PRINT_SUPPRESS_ATTR]: 'true' })}
      {...(blockType && { [PRINT_BLOCK_TYPE_ATTR]: blockType })}
    >
      <Box my={1}>
        <AbstractButton onClick={() => setExpanded(!expanded)} aria-label="Toggle reasoning">
          <Flex
            justifyContent="start"
            align="center"
            sx={{
              '&:hover p': {
                textDecoration: 'underline',
              },
            }}
          >
            <Box {...{ [PRINT_SUPPRESS_ATTR]: true }}>
              <Icon
                type={expanded ? 'chevron-down' : 'chevron-right'}
                size="medium"
                color={tokens.content.text.neutral.base}
              />
            </Box>
            <Text py={1} fontWeight="bold">
              {title}
            </Text>
            {titleExtra}
          </Flex>
        </AbstractButton>
        <Box overflow="hidden" pl={3} pb={expanded ? 2 : 0}>
          <Collapse isOpen={expanded}>
            <Markdown components={renderers} remarkPlugins={[remarkGfm]}>
              {processedValue}
            </Markdown>
          </Collapse>
        </Box>
      </Box>
    </Box>
  );
};

export default React.memo(CollapsibleBlockV3);
